import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { errText } from "@/lib/api";
import { toast } from "sonner";
import { STAGES } from "@/crm/Pipeline";
import EzlynxRating from "@/crm/EzlynxRating";
import LeadDocuments from "@/crm/LeadDocuments";
import { Phone, MessageSquare, Mail, ArrowLeft, AlertTriangle, UserCheck, Trash2 } from "lucide-react";

const inputCls = "w-full h-11 px-3 rounded-sm border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#C5A059]";

function Field({ label, k, type = "text", value, onSave }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</label>
      <input type={type} defaultValue={value ?? ""} onBlur={(e) => onSave(k, type === "number" ? (parseFloat(e.target.value) || null) : e.target.value)} className={inputCls} data-testid={`field-${k}`} />
    </div>
  );
}

export default function LeadProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [agents, setAgents] = useState([]);
  const [note, setNote] = useState("");
  const [fu, setFu] = useState({ type: "Call Follow-Up", due_date: "", note: "" });
  const [task, setTask] = useState("");

  const load = useCallback(() => api.get(`/leads/${id}`).then((r) => setLead(r.data)), [id]);
  useEffect(() => { load(); api.get("/users").then((r) => setAgents(r.data)).catch(() => {}); }, [load]);

  if (!lead) return <div className="p-6">Loading…</div>;
  const name = `${lead.first_name || ""} ${lead.last_name || ""}`.trim() || lead.business_name || "Unnamed Lead";
  const input = "w-full h-11 px-3 rounded-sm border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#C5A059]";
  const savings = (lead.current_premium != null && lead.quoted_premium != null) ? (lead.current_premium - lead.quoted_premium) : null;

  const patch = async (body) => { const { data } = await api.patch(`/leads/${id}`, body); setLead({ ...lead, ...data }); };
  const addNote = async () => { if (!note.trim()) return; await api.post(`/leads/${id}/notes`, { text: note }); setNote(""); load(); toast.success("Note added"); };
  const addFu = async () => { if (!fu.due_date) return toast.error("Pick a date"); await api.post(`/leads/${id}/followups`, fu); setFu({ ...fu, due_date: "", note: "" }); load(); toast.success("Follow-up scheduled"); };
  const addTask = async () => { if (!task.trim()) return; await api.post(`/leads/${id}/tasks`, { title: task }); setTask(""); load(); };
  const toggleTask = async (tid) => { await api.post(`/leads/${id}/tasks/${tid}/toggle`); load(); };
  const convert = async () => {
    try { await api.post("/customers", { lead_id: id, name, phone: lead.phone, email: lead.email, address: lead.address, products: lead.insurance_types }); toast.success("Converted to customer!"); nav("/crm/customers"); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };
  const merge = async () => { await api.post(`/leads/${id}/merge/${lead.possible_duplicate_of}`); load(); toast.success("Marked duplicate merged"); };
  const del = async () => {
    if (!window.confirm("Permanently delete this lead? This cannot be undone.")) return;
    try { await api.delete(`/leads/${id}`); toast.success("Lead deleted"); nav("/crm/leads"); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };

  const save = (k, v) => patch({ [k]: v });

  return (
    <div className="p-4 sm:p-6">
      <button onClick={() => nav(-1)} className="text-sm text-slate-500 flex items-center gap-1 mb-3" data-testid="back-btn"><ArrowLeft size={16} /> Back</button>

      {lead.possible_duplicate_of && (
        <div className="bg-[#C5A059]/15 border border-[#C5A059] rounded-sm p-3 mb-4 flex items-center justify-between" data-testid="dup-banner">
          <span className="flex items-center gap-2 text-sm font-semibold"><AlertTriangle size={16} className="text-[#C5A059]" /> Possible existing customer / duplicate lead.</span>
          <button onClick={merge} className="text-sm font-bold underline" data-testid="merge-btn">Merge records</button>
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold">{name}</h1>
          <p className="text-slate-500 mt-1">{(lead.insurance_types || []).join(", ") || "General"} · Source: {lead.source} {lead.utm_source && `(${lead.utm_source})`}</p>
          {lead.incomplete && <span className="inline-block mt-2 text-xs font-bold text-[#C5A059] bg-[#C5A059]/10 rounded px-2 py-1" data-testid="completion-badge">INCOMPLETE QUOTE · {lead.completion}% COMPLETE</span>}
        </div>
        <div className="flex gap-2">
          <a href={`tel:${lead.phone}`} className="bg-[#0B1E36] text-white px-4 h-10 rounded-sm flex items-center gap-2 text-sm font-semibold" data-testid="action-call"><Phone size={15} /> Call</a>
          <a href={`sms:${lead.phone}`} className="border border-slate-300 px-4 h-10 rounded-sm flex items-center gap-2 text-sm font-semibold" data-testid="action-text"><MessageSquare size={15} /> Text</a>
          <a href={`mailto:${lead.email}`} className="border border-slate-300 px-4 h-10 rounded-sm flex items-center gap-2 text-sm font-semibold" data-testid="action-email"><Mail size={15} /> Email</a>
          <button onClick={convert} className="bg-[#C5A059] text-[#0B1E36] px-4 h-10 rounded-sm flex items-center gap-2 text-sm font-bold" data-testid="convert-btn"><UserCheck size={15} /> Convert</button>
          <button onClick={del} className="border border-red-300 text-red-600 px-4 h-10 rounded-sm flex items-center gap-2 text-sm font-semibold hover:bg-red-50" data-testid="delete-lead-btn"><Trash2 size={15} /> Delete</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* left column */}
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <h3 className="font-display text-lg font-bold mb-4">Contact & Details</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="First Name" k="first_name" value={lead.first_name} onSave={save} /><Field label="Last Name" k="last_name" value={lead.last_name} onSave={save} />
              <Field label="Phone" k="phone" value={lead.phone} onSave={save} /><Field label="Email" k="email" value={lead.email} onSave={save} /><Field label="Address" k="address" value={lead.address} onSave={save} />
              <div><label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</label>
                <select defaultValue={lead.status} onChange={(e) => patch({ status: e.target.value })} className={input} data-testid="field-status">{STAGES.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
              <div><label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Assigned Agent</label>
                <select defaultValue={lead.assigned_agent_id || ""} onChange={(e) => { const a = agents.find((x) => x.id === e.target.value); patch({ assigned_agent_id: e.target.value, assigned_agent_name: a?.name || null }); }} className={input} data-testid="field-agent"><option value="">Unassigned</option>{agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <h3 className="font-display text-lg font-bold mb-4">Quote Information</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Current Carrier" k="current_carrier" value={lead.current_carrier} onSave={save} /><Field label="Current Premium" k="current_premium" type="number" value={lead.current_premium} onSave={save} />
              <Field label="Renewal / Expiration Date" k="renewal_date" type="date" value={lead.renewal_date} onSave={save} />
              <Field label="Quoted Carrier" k="quoted_carrier" value={lead.quoted_carrier} onSave={save} /><Field label="Quoted Premium" k="quoted_premium" type="number" value={lead.quoted_premium} onSave={save} />
              <Field label="Policy Effective Date" k="policy_effective_date" type="date" value={lead.policy_effective_date} onSave={save} /><Field label="Policy Number" k="policy_number" value={lead.policy_number} onSave={save} />
            </div>
            {savings != null && (
              <div className="mt-4 bg-[#0B1E36] text-white rounded-sm p-4" data-testid="estimated-savings">
                <p className="text-xs uppercase tracking-wider text-[#C5A059]">Estimated Savings</p>
                <p className="font-mono text-2xl font-bold">{savings >= 0 ? "$" + savings.toFixed(2) : "-$" + Math.abs(savings).toFixed(2)}</p>
              </div>
            )}
          </div>

          <EzlynxRating leadId={id} lead={lead} />

          <LeadDocuments leadId={id} />

          {Object.keys(lead.answers || {}).length > 0 && (
            <div className="bg-white border border-slate-200 rounded-sm p-5">
              <h3 className="font-display text-lg font-bold mb-3">Submitted Answers</h3>
              <div className="grid sm:grid-cols-2 gap-2 text-sm">
                {Object.entries(lead.answers).map(([k, v]) => v && <div key={k}><span className="text-slate-500 capitalize">{k.replace(/_/g, " ")}: </span><span className="font-medium">{String(v)}</span></div>)}
              </div>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <h3 className="font-display text-lg font-bold mb-3">Notes</h3>
            <div className="flex gap-2 mb-3">
              <input className={input} placeholder="Add a note…" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} data-testid="note-input" />
              <button onClick={addNote} className="bg-[#0B1E36] text-white px-4 rounded-sm font-semibold text-sm" data-testid="note-add">Add</button>
            </div>
            <div className="space-y-2">{(lead.notes || []).slice().reverse().map((n) => <div key={n.id} className="border-l-2 border-[#C5A059] pl-3 py-1"><p className="text-sm">{n.text}</p><p className="text-xs text-slate-400">{n.author} · {new Date(n.created_at).toLocaleString()}</p></div>)}</div>
          </div>
        </div>

        {/* right column */}
        <div className="space-y-5">
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <h3 className="font-display text-lg font-bold mb-3">Schedule Follow-up</h3>
            <select className={`${input} mb-2`} value={fu.type} onChange={(e) => setFu({ ...fu, type: e.target.value })} data-testid="fu-type">
              {["Call Follow-Up", "Text Follow-Up", "Email Follow-Up", "Policy Renewal Follow-Up", "Quote Follow-Up"].map((t) => <option key={t}>{t}</option>)}
            </select>
            <input type="date" className={`${input} mb-2`} value={fu.due_date} onChange={(e) => setFu({ ...fu, due_date: e.target.value })} data-testid="fu-date" />
            <button onClick={addFu} className="w-full bg-[#C5A059] text-[#0B1E36] font-bold h-10 rounded-sm text-sm" data-testid="fu-add">Schedule</button>
            <div className="mt-3 space-y-1">{(lead.follow_ups || []).map((f) => <div key={f.id} className="text-sm border-b border-slate-100 py-1">{f.type} · {new Date(f.due_date).toLocaleDateString()}</div>)}</div>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <h3 className="font-display text-lg font-bold mb-3">Tasks</h3>
            <div className="flex gap-2 mb-3">
              <input className={input} placeholder="New task…" value={task} onChange={(e) => setTask(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTask()} data-testid="task-input" />
              <button onClick={addTask} className="bg-[#0B1E36] text-white px-3 rounded-sm text-sm">+</button>
            </div>
            {(lead.tasks || []).map((t) => <label key={t.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer" data-testid={`task-${t.id}`}><input type="checkbox" checked={t.done} onChange={() => toggleTask(t.id)} /> <span className={t.done ? "line-through text-slate-400" : ""}>{t.title}</span></label>)}
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <h3 className="font-display text-lg font-bold mb-3">Activity History</h3>
            <div className="space-y-2 max-h-72 overflow-auto">
              {(lead.activity || []).map((a) => <div key={a.id} className="text-xs border-b border-slate-100 py-1.5"><span className="font-semibold capitalize">{a.action.replace(/_/g, " ")}</span> {a.detail && `· ${a.detail}`}<span className="block text-slate-400">{a.actor_name} · {new Date(a.created_at).toLocaleString()}</span></div>)}
              {(lead.activity || []).length === 0 && <p className="text-sm text-slate-400">No activity yet.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
